/**
 * BF-B5 / ADR-012 R3.4 — the operator's view of an applicant's ID verification.
 *
 * Applicant IDV is *asked for, never enforced* (ADR-012 R3). Nothing is gated on
 * it, which is exactly why the operator being able to SEE a decline or an
 * incomplete check is not decoration — it is the whole mechanism. The backend
 * has served this since BF-B2 (`applicant-idv-state.ts`, platform#226) and
 * nothing rendered it.
 *
 * WHY THE EVIDENCE TREE ISN'T ENOUGH. Once `applicant_idv_required` is on, the
 * three `identity.*` IDV signals do reach the operator through `evidence_tree`
 * — but only as `verified` or `missing`. That binary cannot tell "chose to opt
 * out" apart from "never touched it", and platform#226's own commit message
 * calls that collapse worse than showing neither. This module is what keeps the
 * distinction.
 *
 * COPY RULES. Operator-facing, so the banned vocabulary applies (CLAUDE.md —
 * no "fail", "risk", "pass", "deny"). Beyond the literal list: an applicant who
 * opts out has made a choice the product explicitly offered them (R3.3), and a
 * check that ran without completing is a workflow fact. Neither is a finding
 * about the person (company constitution, Core Value 1). "Declined" is
 * deliberately avoided too — the Verification Center reserves that word for the
 * operator's own outcome, and reusing it here would read as a decision someone
 * made ABOUT the applicant rather than one the applicant made themselves.
 */

/**
 * Mirrors `ApplicantIdvState` in the platform's `services/applicant-idv-state.ts`.
 *
 * Declared locally rather than imported because `dv-api-contract` v0.1.3 carries
 * the applicant *session* fields (`applicant_idv_required` / `_skipped` /
 * `_verified`) but NOT the operator-facing `applicant_idv_state` on the case
 * review payload. Same shim pattern vouch-assured#110 used, and it should retire
 * the same way #116 retired that one — see `docs/applicant-page-b-backend-handoff.md`.
 */
export type ApplicantIdvState =
  | "not_required"
  | "not_started"
  | "pending"
  | "verified"
  | "failed"
  | "declined";

const KNOWN_STATES: ReadonlySet<string> = new Set<ApplicantIdvState>([
  "not_required",
  "not_started",
  "pending",
  "verified",
  "failed",
  "declined",
]);

/**
 * Runtime guard at the payload boundary. The field arrives untyped (see above),
 * and an unrecognised value must render nothing rather than an empty chip — a
 * blank badge next to a name reads as a state, and a wrong state here is worse
 * than no state at all.
 */
export function parseApplicantIdvState(raw: unknown): ApplicantIdvState | null {
  if (typeof raw !== "string") return null;
  return KNOWN_STATES.has(raw) ? (raw as ApplicantIdvState) : null;
}

export type IdvTone = "verified" | "attention" | "progress" | "neutral";

export interface IdvStateDisplay {
  label: string;
  /** One line of context for the review panel. Null where the label says it all. */
  detail: string | null;
  tone: IdvTone;
}

/**
 * `not_required` is absent on purpose: the org doesn't run applicant IDV, so the
 * applicant was never asked and there is no gap to report. Rendering "not
 * required" would invent a status for orgs that simply don't use the feature.
 */
const DISPLAY: Record<Exclude<ApplicantIdvState, "not_required">, IdvStateDisplay> = {
  verified: {
    label: "ID verified",
    detail: null,
    tone: "verified",
  },
  declined: {
    label: "Applicant opted out",
    detail: "They were offered ID verification and chose not to complete it. This is allowed and costs them one additional vouch — nothing else.",
    tone: "attention",
  },
  failed: {
    label: "ID check incomplete",
    detail: "The check ran but did not come back verified. This is not a finding about the applicant — document quality and lighting are the usual causes.",
    tone: "attention",
  },
  pending: {
    label: "ID check in progress",
    detail: "Started, with no result back yet.",
    tone: "progress",
  },
  not_started: {
    label: "ID check not started",
    detail: "Offered, with no attempt on record yet.",
    tone: "neutral",
  },
};

/**
 * platform#595: on a Flow that REQUIRES the check, the offer-only detail is
 * wrong. "Costs them one additional vouch" was retired for these Flows by
 * ADR-012 R3's second amendment (vouches never substitute), and the case goes
 * to review instead. Labels are unchanged; only the detail line differs.
 */
const REQUIRED_DETAIL: Partial<Record<ApplicantIdvState, string>> = {
  declined:
    "This role requires an identity check. They chose not to complete it, which they are allowed to do, so the case is marked for your review.",
  failed:
    "This role requires an identity check. It ran but did not come back verified, which is usually document quality or a thin record, not a finding about them. The case is marked for your review.",
};

/** Null when there is nothing the operator needs to see. */
export function describeApplicantIdvState(
  state: ApplicantIdvState | null,
  opts: { requiredByFlow?: boolean } = {},
): IdvStateDisplay | null {
  if (!state || state === "not_required") return null;
  const detail = opts.requiredByFlow ? REQUIRED_DETAIL[state] : undefined;
  return detail ? { ...DISPLAY[state], detail } : DISPLAY[state];
}

/**
 * States worth pulling into the queue, where space is scarce. `verified` and
 * `not_started` are omitted deliberately: the first is the happy path and the
 * second is the ordinary state of most in-flight cases, so badging either would
 * put a marker on nearly every row and cost the two that matter their salience.
 */
export function isQueueWorthyIdvState(state: ApplicantIdvState | null): boolean {
  return state === "declined" || state === "failed";
}

// ---------------------------------------------------------------------------
// BF-B4 — name binding (ADR-012 R4)
// ---------------------------------------------------------------------------

export type IdvNameBindingStatus = "match" | "partial" | "mismatch" | "unknown";

export interface IdvNameBinding {
  status: IdvNameBindingStatus;
  confidence?: number;
  /** The name on the application. */
  subject_name?: string | null;
  /** What the ID check came back with. */
  kyc_name_summary?: string | null;
}

const KNOWN_BINDING: ReadonlySet<string> = new Set<IdvNameBindingStatus>([
  "match",
  "partial",
  "mismatch",
  "unknown",
]);

export function parseIdvNameBinding(raw: unknown): IdvNameBinding | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const status = typeof obj.status === "string" && KNOWN_BINDING.has(obj.status)
    ? (obj.status as IdvNameBindingStatus)
    : null;
  if (!status) return null;
  return {
    status,
    confidence: typeof obj.confidence === "number" ? obj.confidence : undefined,
    subject_name: typeof obj.subject_name === "string" ? obj.subject_name : null,
    kyc_name_summary: typeof obj.kyc_name_summary === "string" ? obj.kyc_name_summary : null,
  };
}

/**
 * Only `mismatch` and `partial` surface. ADR-012 R4 is explicit that a name
 * difference is NEVER an automatic adverse outcome — name normalization is hard
 * enough that a false mismatch is likelier than a real one, which is precisely
 * why v1 stops at showing the operator and scores nothing. The copy has to carry
 * that: it states the difference and leaves the judgment where it belongs.
 */
export function describeIdvNameBinding(
  binding: IdvNameBinding | null,
): IdvStateDisplay | null {
  if (!binding) return null;
  if (binding.status === "match" || binding.status === "unknown") return null;

  const names = binding.subject_name && binding.kyc_name_summary
    ? ` Application says "${binding.subject_name}"; the ID check returned "${binding.kyc_name_summary}".`
    : "";

  if (binding.status === "partial") {
    return {
      label: "Name partially matches the ID",
      detail: `Common with middle names, married names, and transliteration.${names}`,
      tone: "neutral",
    };
  }
  return {
    label: "Name differs from the ID",
    detail: `Worth a look, not a verdict — name matching is imprecise, so a false difference is more likely than a real one.${names}`,
    tone: "attention",
  };
}
